
import { Component, Prop, Vue } from "vue-property-decorator";
import DialogBanner from "@/components/app/DialogBanner.vue";
import ConfirmationDialog from "@/components/alert/ConfirmationDialog.vue";
import Notice from "@/domain/restaurant/Notice";
import TimeService from "@/services/TimeService";
import Restaurant from "@/domain/restaurant/Restaurant";
import RemoteServices from "@/services/RemoteServices";
import FormService from "@/services/FormService";

@Component({
  components: { ConfirmationDialog, DialogBanner }
})
export default class NoticeDialog extends Vue {
  @Prop({ default: false })
  value!: boolean;

  loading = false;

  @Prop({ default: [] })
  noticeList!: Notice[];

  @Prop({ default: null })
  selectedRestaurant!: Restaurant | null;

  isNewNotice: boolean = false;

  isUpdateNotice = false;

  noticeCopy: Notice = new Notice();

  @Prop({ default: new Date() })
  date!: Date;

  possibleTypes = Notice.noticeTypes;

  headers = [
    {
      text: this.$t("restaurant.notice.type.label"),
      value: "type",
      groupable: false,
      width: 50
    },
    {
      text: this.$t("restaurant.notice.message.label"),
      value: "message",
      groupable: false,
      class: "text-wrap"
    },
    {
      text: "",
      value: "icons",
      groupable: false,
      width: 100
    }
  ];

  maxSizeRule = [FormService.maxLength255];

  get notice() {
    if (this.noticeList.length === 0) return null;
    return this.noticeList[0];
  }

  get confirmationDialog() {
    return this.$refs.confirmationDialog as ConfirmationDialog;
  }

  get dialog() {
    return this.value;
  }

  set dialog(newValue) {
    this.resetNewNoticeForm();
    this.$emit("input", newValue);
  }

  showNewNoticeForm() {
    this.noticeCopy = new Notice();
    this.isUpdateNotice = false;
    this.isNewNotice = true;
  }

  resetNewNoticeForm() {
    this.isNewNotice = false;
    this.isUpdateNotice = false;
  }

  showUpdateNoticeForm(notice: Notice) {
    this.isNewNotice = false;
    this.noticeCopy = new Notice(notice);
    this.isUpdateNotice = true;
  }

  get formValid(): boolean {
    return (
      this.noticeCopy.type != null &&
      this.noticeCopy.message != null &&
      this.noticeCopy.message !== ""
    );
  }

  set formValid(newVal: boolean) {}

  get formattedDate() {
    return TimeService.getDateDisplayStringWithWeekDay(this.date);
  }

  getNoticeDisplayString(noticeType: string) {
    return Notice.getNoticeTypeFromString(noticeType)?.text;
  }

  getNoticeDisplayIcon(noticeType: string) {
    return Notice.getNoticeTypeFromString(noticeType)?.icon?.icon;
  }

  getNoticeDisplayIconColour(noticeType: string) {
    return Notice.getNoticeTypeFromString(noticeType)?.icon?.colour;
  }

  getNoticeDisplayColour(noticeType: string) {
    return Notice.getNoticeTypeFromString(noticeType)?.colour;
  }

  async createNotice() {
    if (this.selectedRestaurant !== null) {
      this.loading = true;
      this.noticeCopy.date = TimeService.getISODateString(this.date);
      try {
        this.noticeCopy = await RemoteServices.createNotice(
          this.selectedRestaurant!,
          this.noticeCopy
        );
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
      await this.$store.dispatch(
        "success",
        this.$t("restaurant.notice.create.success")
      );
      this.emitNewNotice(this.noticeCopy);
      this.isNewNotice = false;
      this.noticeCopy = new Notice();
    }
    this.loading = false;
  }

  async deleteNotice(notice: Notice) {
    this.loading = true;
    if (
      await this.confirmationDialog.open(
        this.$tc("restaurant.notice.delete.message"),
        this.$t("delete.confirmation") +
          "\n" +
          this.getNoticeDisplayString(notice.type) +
          "\n" +
          this.$t("delete.warning")
      )
    ) {
      if (this.selectedRestaurant !== null && notice.noticeId != null) {
        try {
          await RemoteServices.deleteNotice(this.selectedRestaurant!, notice);
        } catch (error) {
          await this.$store.dispatch("error", error.message);
        }
        await this.$store.dispatch(
          "success",
          this.$t("restaurant.notice.delete.success")
        );
        this.emitDeleteNotice(notice);
      }
    }
    this.loading = false;
  }

  async updateNoticeMessage() {
    this.loading = true;
    if (this.selectedRestaurant !== null && this.noticeCopy.message != null) {
      try {
        this.noticeCopy = await RemoteServices.updateNoticeMessage(
          this.selectedRestaurant!,
          this.noticeCopy
        );
        await this.$store.dispatch(
          "success",
          this.$t("restaurant.notice.update.success")
        );
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
      await this.emitUpdateNotice(this.noticeCopy);
      this.isNewNotice = false;
      this.noticeCopy = new Notice();
      this.resetNewNoticeForm();
    }
    this.loading = false;
  }

  emitNewNotice(notice: Notice) {
    this.$emit("new-notice", this.noticeCopy);
  }

  emitDeleteNotice(notice: Notice) {
    this.$emit("delete-notice", this.noticeCopy);
  }

  async emitUpdateNotice(notice: Notice) {
    await this.emitDeleteNotice(notice);
    await this.emitNewNotice(notice);
  }
}
